import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { Loader, Slider } from "@/shared/ui";
import { useReportPlayerBarHeight } from "@/shared/lib/playerBarHeight";
import { IconPlayerPlay, IconPlayerPause, IconPlayerSkipBack, IconPlayerSkipForward, IconMicrophone, IconX, } from "@tabler/icons-react";
import { usePlayer } from "@/app/providers/PlayerProvider";
import { useMediaQuery } from "@/shared/lib";
import { getAudioPublicToken } from "@/shared/api/songs";
import { SongDetailPanel } from "@/pages/web/MySongsPage/SongDetailPanel";
import styles from "@/widgets/web/web-player/ui/WebPlayer.module.scss";
import sheet from "./GlobalPlayer.module.scss";
export const GlobalPlayer = ({ language: _language, }) => {
    const navigate = useNavigate();
    const { isPlayerOpen, currentTrack, isPlaying, currentTime, duration, isAudioLoading, togglePlayPause, setCurrentTime, audioRef, nextTrack, prevTrack, closePlayer, } = usePlayer();
    // Публикуем высоту бара в --vp-player-h (резерв места снизу под контент).
    const barRef = useRef(null);
    useReportPlayerBarHeight(barRef, isPlayerOpen && !!currentTrack);
    // Bottom-sheet деталей — только на мобильной ширине (≤770px). На широком
    // экране (в т.ч. miniapp на десктопе) детали открываются split-view в
    // «Моей музыке» по тому же брейкпоинту, дублировать поверхность не нужно.
    const isMobileWidth = useMediaQuery("(max-width: 770px)");
    const [failedImages, setFailedImages] = useState(new Set());
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const [titleNavigating, setTitleNavigating] = useState(false);
    // Открыта ли выезжающая снизу панель деталей текущего трека (bottom-sheet).
    const [sheetOpen, setSheetOpen] = useState(false);
    // Свайп-вниз для закрытия шторки тянем ТОЛЬКО за ручку (dragListener=false),
    // иначе перетаскивание перехватывало бы внутренний скролл панели деталей.
    const sheetDragControls = useDragControls();
    // Плеер закрылся / трек без audio_id / ушли с мобильной ширины → закрываем.
    useEffect(() => {
        if (!isPlayerOpen || currentTrack?.audioId == null || !isMobileWidth) {
            setSheetOpen(false);
        }
    }, [isPlayerOpen, currentTrack, isMobileWidth]);
    // Блокируем скролл фона, пока открыта шторка деталей.
    useEffect(() => {
        if (!sheetOpen)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [sheetOpen]);
    // Клик по названию → страница песни (публичный хаб), by-token лениво.
    const handleTitleClick = async () => {
        const audioId = currentTrack?.audioId;
        if (audioId == null || titleNavigating)
            return;
        setTitleNavigating(true);
        try {
            const publicToken = await getAudioPublicToken(audioId);
            navigate(`/song/${publicToken}`);
        }
        catch (e) {
            console.error("GlobalPlayer: failed to resolve public token", e);
        }
        finally {
            setTitleNavigating(false);
        }
    };
    useEffect(() => {
        const calculateScrollbarWidth = () => {
            const outer = document.createElement("div");
            outer.style.visibility = "hidden";
            outer.style.overflow = "scroll";
            outer.style.msOverflowStyle = "scrollbar";
            document.body.appendChild(outer);
            const inner = document.createElement("div");
            outer.appendChild(inner);
            const width = outer.offsetWidth - inner.offsetWidth;
            outer.parentNode?.removeChild(outer);
            setScrollbarWidth(width);
        };
        calculateScrollbarWidth();
        window.addEventListener("resize", calculateScrollbarWidth);
        return () => window.removeEventListener("resize", calculateScrollbarWidth);
    }, []);
    // Синхронизация слайдера с audio элементом
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio)
            return;
        const syncProgress = () => {
            if (audio.duration) {
                setCurrentTime((audio.currentTime / audio.duration) * 100);
            }
        };
        audio.addEventListener("timeupdate", syncProgress);
        audio.addEventListener("seeked", syncProgress);
        return () => {
            audio.removeEventListener("timeupdate", syncProgress);
            audio.removeEventListener("seeked", syncProgress);
        };
    }, [audioRef, setCurrentTime]);
    const formatTime = (progress, durationString) => {
        if (!durationString || durationString === "0:00") {
            if (duration > 0) {
                const totalSeconds = Math.floor(duration);
                const currentSeconds = Math.floor((progress / 100) * totalSeconds);
                const currentMinutes = Math.floor(currentSeconds / 60);
                const currentSecondsRemainder = currentSeconds % 60;
                return `${currentMinutes}:${String(currentSecondsRemainder).padStart(2, "0")}`;
            }
            return "0:00";
        }
        const [minutes, seconds] = durationString.split(":").map(Number);
        const totalSeconds = minutes * 60 + seconds;
        const currentSeconds = Math.floor((progress / 100) * totalSeconds);
        const currentMinutes = Math.floor(currentSeconds / 60);
        const currentSecondsRemainder = currentSeconds % 60;
        return `${currentMinutes}:${String(currentSecondsRemainder).padStart(2, "0")}`;
    };
    const formatDuration = () => {
        if (currentTrack?.duration && currentTrack.duration !== "0:00") {
            return currentTrack.duration;
        }
        if (duration > 0) {
            const totalSeconds = Math.floor(duration);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return `${minutes}:${String(seconds).padStart(2, "0")}`;
        }
        return "0:00";
    };
    const handleSeek = (value) => {
        setCurrentTime(value);
        if (audioRef.current && audioRef.current.duration) {
            audioRef.current.currentTime = (value / 100) * audioRef.current.duration;
        }
    };
    if (!isPlayerOpen || !currentTrack)
        return null;
    const canOpenSheet = currentTrack.audioId != null && isMobileWidth;
    return (_jsxs(_Fragment, { children: [_jsx(AnimatePresence, { children: sheetOpen && canOpenSheet && (_jsxs(_Fragment, { children: [_jsx(motion.div, { className: sheet.overlay, initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 }, onClick: () => setSheetOpen(false) }), _jsxs(motion.div, { className: sheet.sheet, initial: { y: "100%" }, animate: { y: 0 }, exit: { y: "100%" }, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] }, drag: "y", dragListener: false, dragControls: sheetDragControls, dragConstraints: { top: 0, bottom: 0 }, dragElastic: { top: 0, bottom: 0.4 }, onDragEnd: (_, info) => {
                                if (info.offset.y > 120 || info.velocity.y > 600) {
                                    setSheetOpen(false);
                                }
                            }, children: [_jsx("div", { className: sheet.handle, onPointerDown: (e) => sheetDragControls.start(e), "aria-hidden": "true" }), _jsx("div", { className: sheet.body, children: _jsx(SongDetailPanel, { songId: currentTrack.songId, audioId: currentTrack.audioId, onClose: () => setSheetOpen(false) }) })] })] })) }), _jsxs("div", { ref: barRef, className: styles.bar, style: {
                    bottom: "var(--miniapp-navbar-h, 64px)",
                    left: 0,
                    right: scrollbarWidth > 0 ? scrollbarWidth : 0,
                }, children: [canOpenSheet && (_jsx(motion.div, { className: sheet.grabber, drag: "y", dragConstraints: { top: 0, bottom: 0 }, dragElastic: { top: 0.6, bottom: 0 }, onDragEnd: (_, info) => {
                            if (info.offset.y < -40 || info.velocity.y < -300) {
                                setSheetOpen(true);
                            }
                        }, onClick: () => setSheetOpen(true), role: "button", "aria-label": "\u0414\u0435\u0442\u0430\u043B\u0438 \u043F\u0435\u0441\u043D\u0438", children: _jsx("span", { className: sheet.grabberBar }) })), _jsxs("div", { className: styles.inner, children: [_jsxs("div", { className: styles.trackInfo, children: [_jsx("div", { className: styles.cover, children: currentTrack.imageUrl && !failedImages.has(currentTrack.id) ? (_jsx("img", { className: styles.coverImg, src: currentTrack.imageUrl, alt: currentTrack.title, onError: () => setFailedImages((prev) => new Set(prev).add(currentTrack.id)) })) : (_jsx(IconMicrophone, { size: 22, color: "var(--vp-text-dim)" })) }), _jsxs("div", { className: styles.titleStack, children: [currentTrack.audioId != null ? (_jsx("button", { type: "button", className: styles.trackTitleLink, onClick: () => void handleTitleClick(), children: currentTrack.title })) : (_jsx("span", { className: styles.trackTitle, children: currentTrack.title })), currentTrack.date && (_jsx("span", { className: styles.trackMeta, children: currentTrack.date }))] }), _jsx("button", { type: "button", className: sheet.closeBtn, onClick: closePlayer, "aria-label": "\u0417\u0430\u043A\u0440\u044B\u0442\u044C", children: _jsx(IconX, { size: 20 }) })] }), _jsxs("div", { className: styles.controls, children: [_jsx("button", { type: "button", className: `${styles.ctrlBtn} ${styles.prevNext}`, onClick: prevTrack, "aria-label": "Previous", children: _jsx(IconPlayerSkipBack, { size: 18 }) }), _jsx("button", { type: "button", className: `${styles.ctrlBtn} ${styles.playBtn}`, onClick: togglePlayPause, disabled: isAudioLoading, "aria-label": isPlaying ? "Pause" : "Play", children: isAudioLoading ? (_jsx(Loader, { size: "sm" })) : isPlaying ? (_jsx(IconPlayerPause, { size: 22 })) : (_jsx(IconPlayerPlay, { size: 22 })) }), _jsx("button", { type: "button", className: `${styles.ctrlBtn} ${styles.prevNext}`, onClick: nextTrack, "aria-label": "Next", children: _jsx(IconPlayerSkipForward, { size: 18 }) })] }), _jsxs("div", { className: styles.progress, children: [_jsx("span", { className: styles.timeLabel, children: formatTime(currentTime, currentTrack.duration) }), _jsx(Slider, { className: styles.progressSlider, value: currentTime, onChange: handleSeek, max: 100, "aria-label": "Seek" }), _jsx("span", { className: styles.timeLabel, children: formatDuration() })] })] })] })] }));
};
